/**
 * Procedural item name composition. Each item name is assembled from three
 * per-category pools:
 *
 *   [optional Prefix] [Core] [optional Suffix]
 *
 * Pools are sorted mundane → powerful. A score-weighted index with spread
 * picks within each pool, so a score-10 roll lands near the bottom of the
 * pool ("Crude Pistol Mk. I") and a score-100 roll near the top
 * ("Apex Annihilator of the Void"). Prefix/suffix *probability* also rises
 * with score, so low-score rolls stay terse.
 *
 * All content (pools + shared suffixes) lives in this file; adding a word is
 * a one-line change.
 */

import type { UpgradeCategory } from './UpgradeData';
import type { WeaponCategory } from './WeaponData';

interface NamePools {
  prefixes: string[];
  cores: string[];
}

// Shared suffix pool — reads the same across categories (Mk. numbers, titles,
// model codes). Sorted mundane → grandiose.
const SUFFIXES: string[] = [
  'Mk. I', 'Mk. II', 'Mk. III', 'Mk. IV', 'Mk. V', 'Mk. VI', 'Mk. VII', 'Mk. VIII', 'Mk. IX', 'Mk. X',
  'Model A', 'Model B', 'Model C', 'Model D', 'Model Z',
  'Series I', 'Series II', 'Series III', 'Series IX',
  'Prototype', 'Field-Spec', 'Standard-Issue',
  'Plus', 'Advanced', 'Tactical', 'Combat-Grade', 'Elite', 'Deluxe',
  'Prime', 'Paragon', 'Ascendant',
  'of Ruin', 'of the Deep', 'of the Void', 'of the Fall', 'of Dusk', 'of Dawn',
  'of Ascendancy', 'of the Apex', 'of Eternity', 'of Oblivion', 'of the Abyss',
  'Zenith', 'Omega', 'Alpha', 'Ultima', 'Supreme', 'Apex', 'Nemesis',
];

// ─── Upgrade pools (30+ entries per side, per category) ───────────────

const UPGRADE_POOLS: Record<UpgradeCategory, NamePools> = {
  engine: {
    prefixes: [
      'Rusted', 'Worn', 'Crude', 'Basic', 'Standard', 'Balanced', 'Tuned',
      'Calibrated', 'Efficient', 'Reinforced', 'Precision', 'Overclocked',
      'High-Flux', 'Turbo', 'Supercharged', 'Kinetic', 'Mag-Lev', 'Ion-Fed',
      'Plasma-Core', 'Hypersonic', 'Inertial', 'Warp-Driven', 'Flux-Locked',
      'Gravitic', 'Null-Space', 'Singularity', 'Event-Horizon', 'Void-Charged',
      'Apex', 'Transcendent',
    ],
    cores: [
      'Motor', 'Thruster', 'Drive', 'Engine', 'Impeller', 'Propulsor',
      'Turbine', 'Compressor', 'Reactor-Drive', 'Accelerator', 'Pulse-Drive',
      'Jet', 'Ion-Drive', 'Plasma-Drive', 'Flux-Core', 'Warp-Coil',
      'Mag-Drive', 'Inertial-Drive', 'Gravity-Drive', 'Null-Engine',
      'Rift-Engine', 'Singularity-Core', 'Void-Engine', 'Apex-Drive',
      'Transcendence',
    ],
  },
  sensors: {
    prefixes: [
      'Blurry', 'Fogged', 'Basic', 'Standard', 'Clear', 'Sharpened', 'Tuned',
      'Calibrated', 'Wideband', 'Multi-Spectral', 'Phased', 'Pulsed',
      'Harmonic', 'Resonant', 'Polarized', 'Deep-Field', 'Long-Range',
      'Quantum', 'Subspace', 'Farsight', 'All-Seeing', 'Omniscient', 'Oracle',
      'Prescient', 'Augur', 'Ascendant', 'Transcendent', 'Apex',
    ],
    cores: [
      'Lens', 'Spyglass', 'Scope', 'Scanner', 'Sensor', 'Optic', 'Array',
      'Receiver', 'Antenna', 'Reticle', 'Periscope', 'Detector', 'Imager',
      'Spotter', 'Augur', 'Oracle-Eye', 'Farseer', 'Seer', 'Panopticon',
      'Omniscope', 'All-Eye', 'Mindseye', 'Apex-Eye', 'Chronosight',
    ],
  },
  capacitor: {
    prefixes: [
      'Drained', 'Leaking', 'Dim', 'Weak', 'Basic', 'Standard', 'Stable',
      'Primed', 'Charged', 'Dense', 'Layered', 'Overcharged', 'High-Yield',
      'Resonant', 'Ionized', 'Arc', 'Pulse', 'Plasma', 'Fusion', 'Antimatter',
      'Zero-Point', 'Infinite-Loop', 'Singularity', 'Cosmic', 'Astral',
      'Void-Forged', 'Apex', 'Transcendent',
    ],
    cores: [
      'Cell', 'Battery', 'Pack', 'Coil', 'Capacitor', 'Condenser',
      'Accumulator', 'Reactor', 'Power-Cell', 'Core', 'Flux-Cell',
      'Plasma-Cell', 'Ion-Cell', 'Arc-Reactor', 'Fusion-Core',
      'Antimatter-Cell', 'Warp-Cell', 'Singularity-Core', 'Void-Core',
      'Eternity-Core', 'Apex-Reactor', 'Transcendence',
    ],
  },
  shield: {
    prefixes: [
      'Cracked', 'Thin', 'Weak', 'Basic', 'Standard', 'Stable', 'Hardened',
      'Layered', 'Reactive', 'Ablative', 'Deflecting', 'Adaptive', 'Phased',
      'Kinetic', 'Polarized', 'Resonant', 'Aegis-Class', 'Bulwark-Class',
      'Warden', 'Adamant', 'Unbreakable', 'Mythic', 'Paragon', 'Apex',
      'Transcendent',
    ],
    cores: [
      'Buckler', 'Plate', 'Barrier', 'Shield', 'Ward', 'Field', 'Bubble',
      'Deflector', 'Projector', 'Guard', 'Warden', 'Aegis', 'Bulwark',
      'Rampart', 'Firewall', 'Null-Field', 'Stasis-Field', 'Singularity-Ward',
      'Void-Aegis', 'Eternity-Shield', 'Apex-Shield', 'Transcendence',
    ],
  },
  chassis: {
    prefixes: [
      'Dented', 'Rusted', 'Patched', 'Basic', 'Standard', 'Sturdy',
      'Reinforced', 'Ribbed', 'Plated', 'Composite', 'Hardened', 'Titanium',
      'Alloyed', 'Carbon-Laced', 'Exo-Plated', 'Reactive', 'Juggernaut',
      'Dreadnought', 'Colossus', 'Leviathan', 'Behemoth', 'Adamantine',
      'Mythic', 'Apex', 'Transcendent',
    ],
    cores: [
      'Frame', 'Body', 'Skeleton', 'Chassis', 'Hull', 'Shell', 'Armature',
      'Exoskeleton', 'Plating', 'Carapace', 'Bulwark', 'Warframe', 'Warhull',
      'Juggernaut', 'Fortress', 'Bastion', 'Leviathan-Frame', 'Behemoth-Hull',
      'Titan-Frame', 'Apex-Chassis',
    ],
  },
};

// ─── Weapon pools (30+ entries per side, per category) ────────────────

const WEAPON_POOLS: Record<WeaponCategory, NamePools> = {
  ballistic: {
    prefixes: [
      'Rusty', 'Crude', 'Chipped', 'Basic', 'Standard', 'Field-Spec',
      'Precision', 'Reinforced', 'Heavy', 'Rapid', 'Auto', 'Burst',
      'Breaching', 'Tactical', 'Armor-Piercing', 'High-Velocity', 'Magnetic',
      'Gauss', 'Railgun', 'Coilgun', 'Hypersonic', 'Annihilator',
      'Executioner', 'Void-Piercing', 'Apex',
    ],
    cores: [
      'Pistol', 'Revolver', 'Sidearm', 'Carbine', 'Rifle', 'Blaster',
      'Shotgun', 'Autogun', 'Autocannon', 'Machinegun', 'Chaingun',
      'Gatling', 'Minigun', 'Cannon', 'Repeater', 'Stormgun', 'Barrage',
      'Railgun', 'Gauss-Rifle', 'Slugthrower', 'Warcannon', 'Siegegun',
      'Doomrifle', 'Executioner', 'Apex-Cannon',
    ],
  },
  energy: {
    prefixes: [
      'Flickering', 'Sputtering', 'Dim', 'Basic', 'Standard', 'Stable',
      'Coherent', 'Focused', 'Pulsed', 'Arc', 'Ionized', 'Photon', 'Plasma',
      'Particle', 'Laser', 'Beam-Driven', 'High-Yield', 'Disintegration',
      'Annihilation', 'Quantum', 'Singularity', 'Void', 'Transcendent',
      'Apex',
    ],
    cores: [
      'Emitter', 'Caster', 'Beamer', 'Pulser', 'Zapper', 'Ray', 'Beam',
      'Laser', 'Lance', 'Arc-Caster', 'Photon-Lance', 'Plasma-Cannon',
      'Particle-Cannon', 'Disintegrator', 'Fusion-Lance', 'Annihilator',
      'Singularity-Beam', 'Void-Lance', 'Eternity-Ray', 'Apex-Ray',
    ],
  },
  rocket: {
    prefixes: [
      'Crude', 'Basic', 'Standard', 'Field-Spec', 'Guided', 'Tracking',
      'Seeking', 'Homing', 'Cluster', 'Incendiary', 'Fragmenting',
      'Thermobaric', 'High-Explosive', 'Tactical', 'Heavy', 'Siege',
      'Warhead-Class', 'Hellfire', 'Annihilator', 'Apocalypse', 'Doomsday',
      'Apex',
    ],
    cores: [
      'Rocket', 'Projectile', 'Missile', 'Launcher', 'Tube', 'Pod',
      'Warhead', 'Battery', 'Salvo', 'Volley', 'Barrage', 'Fury',
      'Thunderhead', 'Hellfire', 'Doom-Missile', 'Cataclysm',
      'Apocalypse-Warhead', 'Apex-Warhead',
    ],
  },
  bomb: {
    prefixes: [
      'Crude', 'Home-Brew', 'Basic', 'Standard', 'Primed', 'Shaped', 'Frag',
      'Cluster', 'Seismic', 'Incendiary', 'Plasma', 'Gravity', 'Thermobaric',
      'Antimatter', 'Null-Charge', 'Cataclysm', 'Doomsday', 'Apex',
    ],
    cores: [
      'Bomb', 'Charge', 'Explosive', 'Grenade', 'Mine', 'Bombard',
      'Detonator', 'Warhead', 'Payload', 'Device', 'Cluster-Bomb',
      'Seismic-Charge', 'Plasma-Bomb', 'Gravity-Well', 'Antimatter-Device',
      'Null-Bomb', 'Cataclysm-Device', 'Doomsday-Device', 'Apex-Warhead',
    ],
  },
  melee: {
    prefixes: [
      'Chipped', 'Dull', 'Rusted', 'Crude', 'Basic', 'Standard', 'Sharpened',
      'Serrated', 'Jagged', 'Whetted', 'Mono-Edged', 'Vibro', 'Pulse',
      'Plasma', 'Phase', 'Resonant', 'Rending', 'Soul-Bound', 'World-Ender',
      'Worldbreaker', 'Apex', 'Transcendent',
    ],
    cores: [
      'Knife', 'Dagger', 'Shiv', 'Blade', 'Sword', 'Saber', 'Cutlass',
      'Falchion', 'Claw', 'Talon', 'Axe', 'Hatchet', 'Hammer', 'Maul',
      'Flail', 'Mace', 'Halberd', 'Glaive', 'Scythe', 'Warblade', 'Cleaver',
      'Greatsword', 'Obliterator', 'Soul-Reaver', 'Worldsplitter',
      'Apex-Edge',
    ],
  },
};

// ─── Picking logic ────────────────────────────────────────────────────

/**
 * Pick an entry from a pool where the index is biased by score:
 *   score 1   → near bottom of pool
 *   score 50  → middle of pool
 *   score 100 → near top of pool
 * A random spread of ±25% of pool length keeps picks from being deterministic.
 */
function pickByScore(pool: string[], score: number): string {
  if (pool.length === 0) return '';
  const t = (Math.max(1, Math.min(100, score)) - 1) / 99;
  const center = t * (pool.length - 1);
  const spread = Math.max(1, pool.length * 0.25);
  const pick = center + (Math.random() * 2 - 1) * spread;
  const idx = Math.max(0, Math.min(pool.length - 1, Math.round(pick)));
  return pool[idx]!;
}

function poolsFor(kind: 'upgrade' | 'weapon', category: string): NamePools | null {
  if (kind === 'weapon') return WEAPON_POOLS[category as WeaponCategory] ?? null;
  return UPGRADE_POOLS[category as UpgradeCategory] ?? null;
}

/**
 * Compose an item name from category-specific pools, gated by score.
 * Prefix and suffix probabilities rise with score so junk rolls stay terse
 * ("Pistol") and high-score rolls read grand ("Apex Annihilator of the Void").
 */
export function generateItemName(
  kind: 'upgrade' | 'weapon',
  category: string,
  score: number,
): string {
  const pools = poolsFor(kind, category);
  if (!pools) return 'Unknown Part';

  const core = pickByScore(pools.cores, score);

  const prefixChance = Math.min(0.95, 0.3 + score / 150);
  const prefix = Math.random() < prefixChance ? pickByScore(pools.prefixes, score) : '';

  // Suffix kicks in later than prefix — below score 30 it's effectively never.
  const suffixChance = Math.min(0.85, Math.max(0, (score - 25) / 100));
  const suffix = Math.random() < suffixChance ? pickByScore(SUFFIXES, score) : '';

  return [prefix, core, suffix].filter(s => s.length > 0).join(' ').trim();
}
